
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye, User, UserPlus, UserSquare } from 'lucide-react';

import { Teacher } from "@/types/teacher.types";

interface TeachersTableProps {
  teachers: Partial<Teacher>[];
  loading: boolean;
}

const TeachersTable: React.FC<TeachersTableProps> = ({ teachers, loading }) => {
  const navigate = useNavigate();

  const getDepartmentName = (teacher: Partial<Teacher>) => {
    try {
      // Handle case where departments is an array of objects with name property
      if (Array.isArray(teacher.departments) && teacher.departments.length > 0) {
        const firstDept = teacher.departments[0];
        if (typeof firstDept === 'object' && firstDept !== null && 'name' in firstDept) {
          return firstDept.name || 'Not assigned';
        }
        // Handle case where departments is an array of strings
        if (typeof firstDept === 'string') {
          return firstDept || 'Not assigned';
        }
      }
      
      // Handle case where department is a string
      if (typeof teacher.department === 'string') {
        return teacher.department || 'Not assigned';
      }
    } catch (error) {
      console.error('Error getting department name:', error);
    }
    
    return 'Not assigned';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Recent Teachers</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 w-full bg-gray-100 animate-pulse rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Recent Teachers</span>
          <Button size="sm" onClick={() => navigate('/teachers/new')}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Teacher
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!Array.isArray(teachers) || teachers.length === 0 ? (
          <div className="text-center py-6">
            <User className="h-12 w-12 mx-auto text-gray-400" />
            <h3 className="mt-2 text-lg font-medium">No teachers yet</h3>
            <p className="text-sm text-gray-500">Add teachers to see them here</p>
            <Button 
              className="mt-4" 
              size="sm" 
              onClick={() => navigate('/teachers/new')}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Teacher
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teachers.slice(0, 5).map((teacher) => (
                  <TableRow key={teacher.id}>
                    <TableCell>
                      <div className="font-medium">
                        {(teacher.firstName || '')} {(teacher.lastName || '')}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {teacher.id || 'N/A'}
                    </TableCell>
                    <TableCell>{getDepartmentName(teacher)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => navigate(`/teachers/${teacher.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {teachers.length > 5 && (
              <div className="flex justify-center mt-4">
                <Button 
                  variant="link" 
                  onClick={() => navigate('/teachers')}
                >
                  View all teachers
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeachersTable;
